import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../services/supabase';
import { useAuth } from './AuthContext';

const ClientsContext = createContext();

// Sample data for testing
const SAMPLE_CLIENTS = [
  {
    id: 'C1',
    name: 'Juan Pérez',
    hasCurrentAccount: true,
    balance: 5000
  },
  {
    id: 'C2',
    name: 'María García',
    hasCurrentAccount: true,
    balance: -2000
  }
];

export const ClientsProvider = ({ children }) => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { user } = useAuth();

  useEffect(() => {
    if (user) {
      fetchClients();
    }
  }, [user]);

  const fetchClients = async () => {
    try {
      setLoading(true);
      const { data, error: supabaseError } = await supabase
        .from('clients')
        .select('*')
        .eq('auth_user_id', user.id)
        .order('name');

      if (supabaseError) throw supabaseError;

      // Transformar la respuesta al formato que espera la UI
      const formattedClients = (data || []).map(client => ({
        ...client,
        hasCurrentAccount: client.has_current_account
      }));

      setClients(formattedClients);
      setError(null);
    } catch (err) {
      console.error('Error fetching clients:', err);
      setError('Error al cargar los clientes');
    } finally {
      setLoading(false);
    }
  };

  const addClient = async (clientData) => {
    try {
      const { data, error: supabaseError } = await supabase
        .from('clients')
        .insert([{
          name: clientData.name,
          has_current_account: clientData.hasCurrentAccount,
          balance: clientData.balance,
          auth_user_id: user.id,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }])
        .select()
        .single();

      if (supabaseError) throw supabaseError;

      // Transformar la respuesta al formato que espera la UI
      const formattedClient = {
        ...data,
        hasCurrentAccount: data.has_current_account
      };

      setClients(currentClients => [formattedClient, ...currentClients]);
      return formattedClient;
    } catch (err) {
      console.error('Error adding client:', err);
      throw err;
    }
  };

  const updateClient = async (clientId, updates) => {
    try {
      const { data, error: supabaseError } = await supabase
        .from('clients')
        .update({
          name: updates.name,
          has_current_account: updates.hasCurrentAccount,
          balance: updates.balance,
          updated_at: new Date().toISOString()
        })
        .eq('id', clientId)
        .eq('auth_user_id', user.id)
        .select()
        .single();

      if (supabaseError) throw supabaseError;

      // Transformar la respuesta al formato que espera la UI
      const formattedClient = {
        ...data,
        hasCurrentAccount: data.has_current_account
      };

      setClients(currentClients =>
        currentClients.map(client =>
          client.id === clientId ? formattedClient : client
        )
      );
      return formattedClient;
    } catch (err) {
      console.error('Error updating client:', err);
      throw err;
    }
  };

  const value = {
    clients,
    loading,
    error,
    addClient,
    updateClient,
    refreshClients: fetchClients
  };

  return (
    <ClientsContext.Provider value={value}>
      {children}
    </ClientsContext.Provider>
  );
};

export const useClients = () => {
  const context = useContext(ClientsContext);
  if (!context) {
    throw new Error('useClients must be used within a ClientsProvider');
  }
  return context;
};

export default ClientsContext; 